import { isValid, parseISO, differenceInHours } from 'date-fns';

export const isLessThan24HoursAgo = (lastUpdated: string) => {
  if (!lastUpdated) return false; // Treat empty string as not outdated

  const date = parseISO(lastUpdated);
  if (!isValid(date)) return false; // Ensure it's a valid date

  return differenceInHours(new Date(), date) < 24;
};

export type FormatDisplayDateOptions = {
  monthYearOnly?: boolean;
};

/**
 * Pure presentation helper. Accepts ISO `YYYY-MM-DD`, `YYYY-MM`, `MM/YYYY`, or
 * `MM/DD/YYYY` and returns a stable dash separated display string.
 * Default returns `MM-DD-YYYY`. With `monthYearOnly`, returns `MM-YYYY`.
 * Returns the original string when the value cannot be parsed, so unknown
 * inputs are surfaced rather than silently emptied.
 */
export const formatDisplayDate = (value: string | null | undefined, options: FormatDisplayDateOptions = {}): string => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';

  let year: string | undefined;
  let month: string | undefined;
  let day: string | undefined;

  const isoFull = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  const isoShort = /^(\d{4})-(\d{2})$/.exec(trimmed);
  const slashShort = /^(\d{2})\/(\d{4})$/.exec(trimmed);
  const slashFull = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);

  if (isoFull) {
    year = isoFull[1];
    month = isoFull[2];
    day = isoFull[3];
  } else if (isoShort) {
    year = isoShort[1];
    month = isoShort[2];
  } else if (slashFull) {
    month = slashFull[1];
    day = slashFull[2];
    year = slashFull[3];
  } else if (slashShort) {
    month = slashShort[1];
    year = slashShort[2];
  } else {
    return trimmed;
  }

  if (!year || !month) return trimmed;

  if (options.monthYearOnly || !day) {
    return `${month}-${year}`;
  }
  return `${month}-${day}-${year}`;
};
